import React, { useState, useEffect } from 'react';
import logoHeader from '../../assets/images/logos/logo-digitalfactura-header.png';
import './Navbar.css';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Efecto de sombra al hacer scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Bloquear scroll de la página cuando el menú móvil esté abierto
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Cerrar menú con Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  const handleLinkClick = (e, targetId) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    const targetEl = document.querySelector(targetId);
    if (targetEl) {
      const headerOffset = 70;
      const elementPosition = targetEl.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <header className={`df-navbar-wrapper ${isScrolled ? 'df-navbar-scrolled' : ''}`}>
        <div className="df-navbar-container">
          {/* Logo Principal */}
          <a href="#top" className="df-navbar-brand" onClick={(e) => handleLinkClick(e, '#top')}>
            <img
              src={logoHeader}
              alt="Digital Factura - Proveedor Autorizado de Certificación PAC"
              className="df-navbar-logo"
            />
          </a>

          {/* Menú de Escritorio */}
          <nav className="df-navbar-menu-desktop" aria-label="Navegación principal">
            <a
              href="#timbrado"
              className="df-nav-link"
              onClick={(e) => handleLinkClick(e, '#timbrado')}
            >
              Timbrado
            </a>
            <a
              href="#sicai"
              className="df-nav-link"
              onClick={(e) => handleLinkClick(e, '#sicai')}
            >
              SICAI
            </a>
            <a
              href="#contacto"
              className="df-nav-link"
              onClick={(e) => handleLinkClick(e, '#contacto')}
            >
              Contacto
            </a>
          </nav>

          {/* Acciones (Teléfonos y Botón Facturar) */}
          <div className="df-navbar-actions">
            <div className="df-navbar-phones">
              <svg className="df-phone-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
              </svg>
              <a href="tel:3336227809" title="Llamar a soporte técnico">33 3622 7809</a>
              <span className="df-phone-separator">ó</span>
              <a href="tel:3336367965" title="Llamar a ventas">33 3636 7965</a>
            </div>

            <a
              href="https://www.digitalfactura.com/sicaiwebsys/#/login"
              target="_blank"
              rel="noopener noreferrer"
              className="df-btn-factura"
            >
              Factura aquí
            </a>

            {/* Botón Hamburguesa Móvil */}
            <button
              type="button"
              className={`df-hamburger-btn ${isMobileMenuOpen ? 'is-active' : ''}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Cerrar menú de navegación" : "Abrir menú de navegación"}
              aria-expanded={isMobileMenuOpen}
            >
              <span className="df-hamburger-icon">
                <span className="df-hamburger-line"></span>
                <span className="df-hamburger-line"></span>
                <span className="df-hamburger-line"></span>
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Drawer Móvil (Off-canvas) */}
      <div
        className={`df-mobile-drawer-backdrop ${isMobileMenuOpen ? 'is-open' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
      />

      <div
        className={`df-mobile-drawer ${isMobileMenuOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menú móvil"
        aria-hidden={!isMobileMenuOpen}
      >
        <div className="df-drawer-header">
          <img src={logoHeader} alt="Digital Factura" style={{ height: '34px', width: 'auto' }} />
          <button
            type="button"
            className="df-drawer-close"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Cerrar menú"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <nav className="df-drawer-links">
          <a href="#top" className="df-drawer-link" onClick={(e) => handleLinkClick(e, '#top')}>
            Inicio
          </a>
          <a href="#timbrado" className="df-drawer-link" onClick={(e) => handleLinkClick(e, '#timbrado')}>
            Timbrado Corporativo & JSON
          </a>
          <a href="#sicai" className="df-drawer-link" onClick={(e) => handleLinkClick(e, '#sicai')}>
            Familia SICAI (Web, Pro, Server)
          </a>
          <a href="#deconect" className="df-drawer-link" onClick={(e) => handleLinkClick(e, '#deconect')}>
            Herramientas & Descargas
          </a>
          <a href="#contacto" className="df-drawer-link" onClick={(e) => handleLinkClick(e, '#contacto')}>
            Contacto & Soporte
          </a>
        </nav>

        <div className="df-drawer-divider"></div>

        <div className="df-drawer-footer">
          <div className="df-drawer-phone-box">
            <span className="df-drawer-phone-title">Líneas de Atención</span>
            <a href="tel:3336227809" className="df-drawer-phone-link">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
              </svg>
              33 3622 7809
            </a>
            <a href="tel:3336367965" className="df-drawer-phone-link">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
              </svg>
              33 3636 7965
            </a>
          </div>

          <a
            href="https://www.digitalfactura.com/sicaiwebsys/#/login"
            target="_blank"
            rel="noopener noreferrer"
            className="df-btn df-btn-primary df-btn-full"
          >
            Facturar Aquí
          </a>
        </div>
      </div>
    </>
  );
};

export default Navbar;
